import { HttpClient, HttpResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import {
  AssessmentAnswerInterface,
  AssessmentQuestionGroupInterface,
  AssessmentQuestionInterface,
  EntityType,
} from "app/assessment/models";
import { getDeepCopyOfObject } from "app/shared/utilities/utility-methods.utility";
import {
  DropzoneComponent,
  DropzoneConfigInterface,
} from "ngx-dropzone-wrapper";
import { Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";

@Component({
  selector: "cz-photo-upload",
  templateUrl: "./photo-upload.component.html",
  styleUrls: ["./photo-upload.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhotoUploadComponent implements OnInit, OnDestroy {
  @ViewChild("dropzoneRef") public dropzoneRef?: DropzoneComponent;
  @Input() public readonly placeholderImage: string;
  @Input() public readonly parentComponent: string;
  @Input() public readonly questionGroup: AssessmentQuestionGroupInterface;
  @Input() public readonly form: FormGroup;
  @Input() public readonly userId: number;
  @Input() public readonly readOnly: boolean;
  @Input() public readonly type: string;
  @Output() public photoRemoved: EventEmitter<AssessmentQuestionInterface> =
    new EventEmitter<AssessmentQuestionInterface>();
  public dropzoneConfig: DropzoneConfigInterface;
  public disableDropzone: boolean = false;
  public questionGroupCopy: AssessmentQuestionGroupInterface;
  public errorMessage: string;
  @Input() public taskId: number;
  private imageId: number;
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.configureDropzone();

    this.questionGroupCopy =
      this.questionGroup && getDeepCopyOfObject(this.questionGroup);
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public configureDropzone(question?: AssessmentQuestionInterface): void {
    // all hard-coded rules are applied globally throughout the app
    this.dropzoneConfig = {
      url: this.getEndpoint(this.parentComponent, question),
      maxFilesize: 5, // MB
      maxFiles: 1, // number of files accepted
      acceptedFiles: "image/jpeg,image/png", // Only JPG and PNG files accepted
      createImageThumbnails: false, // don't create a preview image
      parallelUploads: 1, // only 1 photo in parallel since we need unique endpoints for each
    };

    this.disableDropzone = this.getQuestionWithoutImage() === 999;
  }

  public onUploadSuccess(event: Event): void {
    this.errorMessage = null;
    const imagePath: string = event[1];

    this.updateForm(imagePath);
    this.configureDropzone();
  }

  public onUploadFailure(error: Error): void {
    const raw = error[1];
    const message =
      typeof raw === "string"
        ? raw
        : raw && raw.message
          ? raw.message
          : "Upload failed.";
    this.errorMessage = message.replace(/MiB/gi, "MB");
    this._cdr.markForCheck();
    this.dropzoneRef.directiveRef.reset();
  }

  public removeFile(
    question: AssessmentQuestionInterface,
    imagePath: string,
  ): void {
    this.errorMessage = null;
    this.deletePhoto(question, imagePath)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.parentComponent === EntityType.EVALUATION) {
          this.photoRemoved.emit();
        }

        // Wait for the successful removal before updating the form and re-configuring.
        this.updateForm();
        this.configureDropzone(question);
        this._cdr.markForCheck();
      });
  }

  public onKeyPress(
    value: string,
    formGroupName: string,
    formControlName: string,
    answer: AssessmentAnswerInterface,
  ): void {
    let formGroup: any = this.form.controls[this.questionGroup.id];
    if (this.type) {
      formGroup = this.form.controls[this.type];
    }
    const updatedAnswer: AssessmentAnswerInterface =
      getDeepCopyOfObject(answer);
    updatedAnswer.text = value.toString();

    if (formGroupName) {
      formGroup.controls[formGroupName].controls[formControlName].setValue(
        updatedAnswer,
      );
      return;
    }

    formGroup.controls[formControlName].setValue(updatedAnswer);
  }

  private updateForm(imagePath?: string): void {
    const questionGroupFormGroup: FormGroup = this.form.controls[
      this.questionGroupCopy.id
    ] as FormGroup;
    if (imagePath) {
      questionGroupFormGroup.controls[this.imageId].setValue({
        id: 1,
        isSelected: true,
        text: imagePath,
      });
    } else {
      // clear image
      questionGroupFormGroup.controls[this.imageId].setValue("");
      // clear optional text
      questionGroupFormGroup.controls[
        `${this.imageId}-${this.imageId + 1}`
      ].setValue("");
    }

    // updates the gallery in the view
    const questions: AssessmentQuestionInterface[] =
      this.questionGroupCopy && this.questionGroupCopy.questions;
    for (const key in questions) {
      if (
        !questions.hasOwnProperty(key) ||
        questions[key].id !== this.imageId
      ) {
        continue;
      }

      if (imagePath) {
        questions[key].answers[0].text = imagePath;
        questions[key].answers[0].isSelected = true;
        continue;
      }

      questions[key].answers[0].text = "";
      questions[key].answers[0].isSelected = false;
      // Remove image lable if image removed
      questions[key].childQuestions[0].answers[0].text = "";
    }

    // If dropzone is rendered, reset it.
    if (this.dropzoneRef) {
      this.dropzoneRef.directiveRef.reset();
    }

    questionGroupFormGroup.markAsDirty();
  }

  private getEndpoint(
    componentName: string,
    question?: AssessmentQuestionInterface,
  ): string {
    if (typeof componentName === "undefined") {
      return null;
    }

    this.imageId = null;
    if (question) {
      this.imageId = question.id;
    } else {
      this.imageId = this.getQuestionWithoutImage();
    }
    let addTaskId: string = "";
    if (this.taskId > 0) {
      addTaskId = `${this.taskId}`;
    }

    // mapping parent components to endpoints
    const endpoints: { [id: string]: string } = {
      [EntityType.SURVEY]: `api/users/${this.userId}/surveys/current/images/${this.imageId}/${addTaskId}`,
      [EntityType.EVALUATION]: `api/users/${this.userId}/evaluations/images/`,
    };
    return endpoints[componentName];
  }

  private getQuestionWithoutImage(): number {
    const questions: {} = this.form && this.form.value["EPIC"];
    for (const key in questions) {
      if (!questions.hasOwnProperty(key) || (key && /[-]/.test(key))) {
        continue;
      }

      if (!questions[key]) {
        return Number(key);
      }
    }

    // if all questions have answers, return 999 since dropzone needs a URL
    return 999;
  }

  private deletePhoto(
    question: AssessmentQuestionInterface,
    imagePath: string,
  ): Observable<any> {
    let encodedImage: string = "";
    if (this.parentComponent === EntityType.EVALUATION) {
      encodedImage = imagePath.substring(imagePath.lastIndexOf("/") + 1);
    }
    const url: string = `${this.getEndpoint(
      this.parentComponent,
      question,
    )}${encodedImage}`;
    return this._httpClient.delete(url, { observe: "response" }).pipe(
      map((httpResponse: HttpResponse<any>) => {
        return httpResponse.body;
      }),
    );
  }
}
